/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { Order, Reservation, RestaurantCustomer } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import {
  formatCurrency,
  formatDate,
  formatTagLabel,
  formatTime,
  getReliabilityColor,
  splitShortDate,
} from "../../utils";
import { ActiveSection, RESERVATION_STATUS_COLORS } from "./constants";
import {
  formatOrderId,
  orderStatusLabel,
  reservationStatusLabel,
} from "./customerDetailHelpers";
import { useAuth } from "@app/vue/providers/Auth";

type StatCard = {
  label: string;
  value: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
};

type FavouriteItem = {
  name: string;
  count: number;
  totalSpent: number;
};

export const CustomerCRMOverview = defineComponent({
  name: "CustomerCRMOverview",
  props: {
    customer: { type: Object as PropType<RestaurantCustomer>, required: true },
    tags: { type: Array as PropType<string[]>, required: true },
    statCards: { type: Array as PropType<StatCard[]>, required: true },
    upcomingReservations: {
      type: Array as PropType<Reservation[]>,
      required: true,
    },
    recentOrders: { type: Array as PropType<Order[]>, required: true },
    favouriteItems: {
      type: Array as PropType<FavouriteItem[]>,
      required: true,
    },
    reviewRequests: { type: Array as PropType<any[]>, required: true },
    loadingOrders: { type: Boolean, required: true },
    loadingReservations: { type: Boolean, required: true },
    allOrdersCount: { type: Number, required: true },
    allReservationsCount: { type: Number, required: true },
    onStartEditing: { type: Function as PropType<() => void>, required: true },
    onSectionChange: {
      type: Function as PropType<(section: ActiveSection) => void>,
      required: true,
    },
  },
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();

    const loadingBlock = (tone: string) => (
      <div class="p-4 flex justify-center">
        <div
          class={`w-5 h-5 border-2 ${tone}/20 border-t-current rounded-full animate-spin`}
        />
      </div>
    );

    return () => (
      <div class="space-y-5 max-w-4xl">
        <div class="grid grid-cols-2 xl:grid-cols-4 gap-2.5">
          {props.statCards.map((card) => (
            <div
              key={card.label}
              class={`${card.bg} rounded-xl p-3.5 border ${card.border} shadow-sm`}
            >
              <div class="flex items-center gap-1 text-[10px] text-base-content/40 uppercase tracking-wider mb-1.5">
                <span
                  class={`material-symbols-outlined text-[12px] ${card.color}`}
                >
                  {card.icon}
                </span>
                {card.label}
              </div>
              <div class={`text-xl font-bold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        <div class="bg-base-100/40 rounded-xl p-4 border border-base-content/5">
          <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider mb-2 flex items-center gap-1">
            <span class="material-symbols-outlined text-[12px]">label</span>
            {t("reservations.customers.tags")}
          </h3>
          {props.tags.length > 0 ? (
            <div class="flex flex-wrap gap-1.5">
              {props.tags.map((tag) => (
                <span key={tag} class="badge badge-sm badge-ghost">
                  {formatTagLabel(tag)}
                </span>
              ))}
            </div>
          ) : (
            <button
              onClick={props.onStartEditing}
              class="text-xs text-base-content/30 hover:text-primary transition-colors duration-100 flex items-center gap-1 min-h-[32px] px-2 -ml-2 rounded-lg hover:bg-primary/5"
            >
              <span class="material-symbols-outlined text-[12px]">add</span>
              {t("reservations.customers.add_tags")}
            </button>
          )}
        </div>

        <div class="bg-base-100/40 rounded-xl border border-base-content/5 overflow-hidden">
          <div class="px-4 py-3 flex items-center justify-between border-b border-base-content/5">
            <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider flex items-center gap-1.5">
              <span class="material-symbols-outlined text-[13px] text-primary">
                upcoming
              </span>
              {t("reservations.customers.upcoming_reservations")}
            </h3>
            {props.allReservationsCount > 0 && (
              <button
                onClick={() => props.onSectionChange("reservations")}
                class="text-[10px] text-primary hover:text-primary/80"
              >
                {t("reservations.customers.view_all")}
              </button>
            )}
          </div>
          {props.loadingReservations ? (
            loadingBlock("text-primary")
          ) : props.upcomingReservations.length > 0 ? (
            <div class="divide-y divide-base-content/5">
              {props.upcomingReservations.map((r: any) => (
                <div
                  key={r.id}
                  class="px-4 py-3 flex items-center gap-3 hover:bg-base-100/30 transition-colors duration-75"
                >
                  <div class="w-10 h-10 rounded-lg bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                    {(() => {
                      const [month, day] = splitShortDate(r.time || r.date);
                      return (
                        <>
                          <span class="text-[10px] font-bold text-primary leading-none">
                            {month}
                          </span>
                          <span class="text-sm font-bold text-primary leading-none">
                            {day}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-medium text-accent">
                        {r.party_name ||
                          props.customer.name ||
                          t("reservations.customers.reservation")}
                      </span>
                      <span
                        class={`badge badge-xs ${RESERVATION_STATUS_COLORS[r.status || ""] || "badge-ghost"}`}
                      >
                        {reservationStatusLabel(r.status, t)}
                      </span>
                    </div>
                    <div class="text-[11px] text-base-content/35 flex items-center gap-2 mt-0.5">
                      {r.time && <span>{formatTime(r.time)}</span>}
                      {r.party_size && (
                        <span>
                          {t("reservations.customers.guests_count", [
                            r.party_size,
                          ])}
                        </span>
                      )}
                      {r.table_name && <span>{r.table_name}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div class="px-4 py-5 text-center">
              <span class="material-symbols-outlined text-xl text-base-content/15">
                event_busy
              </span>
              <p class="text-[11px] text-base-content/25 mt-1">
                {t("reservations.customers.no_upcoming_reservations")}
              </p>
            </div>
          )}
        </div>

        <div class="bg-base-100/40 rounded-xl border border-base-content/5 overflow-hidden">
          <div class="px-4 py-3 flex items-center justify-between border-b border-base-content/5">
            <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider flex items-center gap-1.5">
              <span class="material-symbols-outlined text-[13px] text-info">
                receipt_long
              </span>
              {t("reservations.customers.recent_orders")}
            </h3>
            {props.allOrdersCount > 0 && (
              <button
                onClick={() => props.onSectionChange("orders")}
                class="text-[10px] text-primary hover:text-primary/80"
              >
                {t("reservations.customers.view_all_count", [
                  props.allOrdersCount,
                ])}
              </button>
            )}
          </div>
          {props.loadingOrders ? (
            loadingBlock("text-info")
          ) : props.recentOrders.length > 0 ? (
            <div class="divide-y divide-base-content/5">
              {props.recentOrders.map((order: any) => (
                <div
                  key={order.id}
                  class="px-4 py-2.5 flex items-center gap-3 hover:bg-base-100/30 transition-colors duration-75"
                >
                  <div class="w-8 h-8 rounded-lg bg-info/8 flex items-center justify-center flex-shrink-0">
                    <span class="material-symbols-outlined text-[14px] text-info">
                      receipt_long
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-xs font-medium text-accent">
                      {t("reservations.customers.order_number", [
                        formatOrderId(order.id),
                      ])}
                    </div>
                    <div class="text-[10px] text-base-content/30">
                      {formatDate(order.created_timestamp || order.created_at)}
                      {order.service_method && ` · ${order.service_method}`}
                    </div>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <div class="text-sm font-semibold text-success/80 tabular-nums">
                      {formatCurrency(
                        order.amount || order.total,
                        auth.user.value?.restaurant.currency || "CHF",
                      )}
                    </div>
                    {order.status && (
                      <span
                        class={`badge badge-xs ${order.status === "completed" ? "badge-success badge-outline" : order.status === "cancelled" ? "badge-error badge-outline" : "badge-ghost"}`}
                      >
                        {orderStatusLabel(order.status, t)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div class="px-4 py-5 text-center">
              <span class="material-symbols-outlined text-xl text-base-content/15">
                receipt_long
              </span>
              <p class="text-[11px] text-base-content/25 mt-1">
                {t("reservations.customers.no_orders_yet")}
              </p>
            </div>
          )}
        </div>

        {props.favouriteItems.length > 0 && (
          <div class="bg-base-100/40 rounded-xl border border-base-content/5 overflow-hidden">
            <div class="px-4 py-3 border-b border-base-content/5">
              <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider flex items-center gap-1.5">
                <span class="material-symbols-outlined text-[13px] text-warning">
                  favorite
                </span>
                {t("reservations.customers.favourite_items")}
              </h3>
            </div>
            <div class="divide-y divide-base-content/5">
              {props.favouriteItems.map((item, i) => (
                <div
                  key={item.name}
                  class="px-4 py-2.5 flex items-center gap-3 hover:bg-base-100/30 transition-colors duration-75"
                >
                  <div class="w-7 h-7 rounded-lg bg-warning/8 flex items-center justify-center flex-shrink-0">
                    <span class="text-xs font-bold text-warning/60">
                      #{i + 1}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-xs font-medium text-accent truncate">
                      {item.name}
                    </div>
                    <div class="text-[10px] text-base-content/30">
                      {t(
                        item.count === 1
                          ? "reservations.customers.ordered_once"
                          : "reservations.customers.ordered_count",
                        [item.count],
                      )}
                    </div>
                  </div>
                  <div class="text-xs font-semibold text-success/70 tabular-nums flex-shrink-0">
                    {formatCurrency(
                      item.totalSpent,
                      auth.user.value?.restaurant.currency || "CHF",
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {props.reviewRequests.length > 0 && (
          <div class="bg-base-100/40 rounded-xl border border-base-content/5 overflow-hidden">
            <div class="px-4 py-3 border-b border-base-content/5">
              <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider flex items-center gap-1.5">
                <span class="material-symbols-outlined text-[13px] text-amber-400">
                  rate_review
                </span>
                {t("reservations.customers.review_requests_sent")}
              </h3>
            </div>
            <div class="divide-y divide-base-content/5">
              {props.reviewRequests.map((rr: any) => (
                <div key={rr.id} class="px-4 py-2.5 flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-amber-500/8 flex items-center justify-center flex-shrink-0">
                    <span class="material-symbols-outlined text-[14px] text-amber-400/60">
                      mail
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-xs font-medium text-accent">
                      {t("reservations.customers.review_request_email")}
                    </div>
                    <div class="text-[10px] text-base-content/30">
                      {rr.customer_email
                        ? t("reservations.customers.sent_to", [
                            formatDate(rr.sent_at),
                            rr.customer_email,
                          ])
                        : t("reservations.customers.sent_on", [
                            formatDate(rr.sent_at),
                          ])}
                    </div>
                  </div>
                  {rr.google_place_id && (
                    <span class="badge badge-xs badge-ghost gap-0.5">
                      <span class="material-symbols-outlined text-[10px]">
                        location_on
                      </span>
                      Google
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div class="bg-base-100/40 rounded-xl p-4 border border-base-content/5 space-y-3">
          <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider">
            {t("reservations.customers.reservation_breakdown")}
          </h3>
          <div class="flex items-center gap-6">
            <div class="text-center">
              <div class="text-lg font-bold text-success">
                {props.customer.total_reservations_completed ?? 0}
              </div>
              <div class="text-[10px] text-base-content/30">
                {t("reservations.customers.completed")}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-warning">
                {props.customer.total_reservations_cancellations ?? 0}
              </div>
              <div class="text-[10px] text-base-content/30">
                {t("reservations.customers.cancelled")}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-error">
                {props.customer.total_reservations_noshows ?? 0}
              </div>
              <div class="text-[10px] text-base-content/30">
                {t("reservations.customers.no_shows")}
              </div>
            </div>
          </div>
          {(() => {
            const completed = props.customer.total_reservations_completed ?? 0;
            const noshows = props.customer.total_reservations_noshows ?? 0;
            const cancelled =
              props.customer.total_reservations_cancellations ?? 0;
            const resolved = completed + noshows + cancelled;
            if (resolved === 0) return null;
            const pct = Math.round((completed / resolved) * 100);
            const colors = getReliabilityColor(pct);
            return (
              <div class="pt-2 border-t border-base-content/5">
                <div class="flex items-center justify-between text-[10px]">
                  <span class="text-base-content/30">
                    {t("reservations.customers.reliability")}
                  </span>
                  <span class={`font-semibold ${colors.text}`}>{pct}%</span>
                </div>
                <div class="w-full h-1.5 bg-base-content/5 rounded-full mt-1 overflow-hidden">
                  <div
                    class={`h-full rounded-full transition-[width] duration-300 ease-out ${colors.bg}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  },
});
